import type {
    ICreatePurchaseIntent,
    IIntentCheckoutSessionResponse,
    IPurchaseIntentResponse,
} from '@escloud/types';

import type { ApiClient } from './api-client';

import { getApiUrlForRegion } from '@/lib/region-config';

/**
 * Purchase intents (funnel v2, ESCLOUD-332). An intent lives in the database of
 * the region the buyer chose on the account step, and there is no tenant yet to
 * put in context, so every call names its region and the client addresses that
 * region's API directly through `{ baseUrl }`.
 * Reading a region's intent from another region's API would answer 404 and
 * read as "nothing to resume".
 */
export class PurchaseIntentsService {
    constructor(private api: ApiClient) {}

    private target(region: string) {
        return { baseUrl: getApiUrlForRegion(region) };
    }

    /** Upsert the caller's single open intent in `region` (`POST /purchase-intents`). */
    create(region: string, data: ICreatePurchaseIntent): Promise<IPurchaseIntentResponse> {
        return this.api.post<IPurchaseIntentResponse>('/purchase-intents', data, this.target(region));
    }

    /** The caller's open intent in `region`; 404 `no_open_intent` when there is none. */
    getCurrent(region: string): Promise<IPurchaseIntentResponse> {
        return this.api.get<IPurchaseIntentResponse>('/purchase-intents/current', this.target(region));
    }

    /** One intent by id; 404 `intent_not_found` when absent or not the caller's. */
    get(region: string, intentId: string): Promise<IPurchaseIntentResponse> {
        return this.api.get<IPurchaseIntentResponse>(`/purchase-intents/${intentId}`, this.target(region));
    }

    /**
     * A Checkout Session for the intent: `elements` with a client secret, or
     * `activated` with the new tenant when there was nothing to pay (free plan,
     * mock billing). Re-creates an expired session transparently.
     */
    createCheckoutSession(region: string, intentId: string): Promise<IIntentCheckoutSessionResponse> {
        return this.api.post<IIntentCheckoutSessionResponse>(
            `/purchase-intents/${intentId}/checkout-session`,
            undefined,
            this.target(region)
        );
    }

    /** Best-effort abandon (region switch). 409 `intent_not_open` once completed. */
    abandon(region: string, intentId: string): Promise<void> {
        return this.api.delete<void>(`/purchase-intents/${intentId}`, this.target(region));
    }
}
